import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

/** Dots per side. 4 dots → a 3 × 3 grid of boxes. */
export const DB_DOTS = 4;
export const DB_BOXES = DB_DOTS - 1;
/** Lines along the top of each box row: DB_DOTS rows × DB_BOXES columns. */
export const DB_H_COUNT = DB_DOTS * DB_BOXES;
export const DB_V_COUNT = DB_BOXES * DB_DOTS;

export type EdgeKind = "h" | "v";

export interface DotsBoxesState extends BaseState {
	kind: "dotsboxes";
	/** Who drew each horizontal line, `null` if it is still empty. */
	h: (PlayerIndex | null)[];
	v: (PlayerIndex | null)[];
	/** Who owns each box, row-major. */
	boxes: (PlayerIndex | null)[];
	lastEdge: { kind: EdgeKind; index: number } | null;
}

export interface DotsBoxesMove {
	edge: EdgeKind;
	index: number;
}

const boxIndex = (row: number, col: number): number => row * DB_BOXES + col;

/** The boxes (0, 1 or 2 of them) that this line forms a side of. */
function touchedBoxes(edge: EdgeKind, index: number): number[] {
	if (edge === "h") {
		const row = Math.floor(index / DB_BOXES);
		const col = index % DB_BOXES;
		const boxes: number[] = [];
		if (row > 0) boxes.push(boxIndex(row - 1, col));
		if (row < DB_BOXES) boxes.push(boxIndex(row, col));
		return boxes;
	}
	const row = Math.floor(index / DB_DOTS);
	const col = index % DB_DOTS;
	const boxes: number[] = [];
	if (col > 0) boxes.push(boxIndex(row, col - 1));
	if (col < DB_BOXES) boxes.push(boxIndex(row, col));
	return boxes;
}

function isBoxClosed(h: (PlayerIndex | null)[], v: (PlayerIndex | null)[], box: number): boolean {
	const row = Math.floor(box / DB_BOXES);
	const col = box % DB_BOXES;
	const top = h[row * DB_BOXES + col];
	const bottom = h[(row + 1) * DB_BOXES + col];
	const left = v[row * DB_DOTS + col];
	const right = v[row * DB_DOTS + col + 1];
	return top !== null && bottom !== null && left !== null && right !== null;
}

export const dotsBoxes: GameDefinition<DotsBoxesState, DotsBoxesMove> = {
	id: "dotsboxes",
	category: "classics",
	title: "Dots and Boxes",
	emoji: "🟦",
	blurb: "Join the dots. Close a box to claim it — and go again.",
	howTo: [
		"Take turns drawing one line between two dots.",
		"Close the fourth side of a box and it's yours.",
		"Claiming a box earns you another turn.",
		"Most boxes wins when every line is drawn.",
	],

	create() {
		return {
			kind: "dotsboxes",
			h: Array<PlayerIndex | null>(DB_H_COUNT).fill(null),
			v: Array<PlayerIndex | null>(DB_V_COUNT).fill(null),
			boxes: Array<PlayerIndex | null>(DB_BOXES * DB_BOXES).fill(null),
			lastEdge: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { edge, index } = raw as { edge?: unknown; index?: unknown };
		if (edge !== "h" && edge !== "v") return null;
		if (typeof index !== "number" || !Number.isInteger(index) || index < 0) return null;
		if (index >= (edge === "h" ? DB_H_COUNT : DB_V_COUNT)) return null;
		return { edge, index };
	},

	apply(state, player, move): MoveOutcome<DotsBoxesState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const lines = move.edge === "h" ? state.h : state.v;
		if (lines[move.index] !== null) return reject("That line is already drawn.");

		const h = move.edge === "h" ? [...state.h] : state.h;
		const v = move.edge === "v" ? [...state.v] : state.v;
		(move.edge === "h" ? h : v)[move.index] = player;

		const boxes = [...state.boxes];
		const scores: [number, number] = [...state.scores];
		let claimed = 0;
		for (const box of touchedBoxes(move.edge, move.index)) {
			if (boxes[box] === null && isBoxClosed(h, v, box)) {
				boxes[box] = player;
				scores[player] += 1;
				claimed += 1;
			}
		}

		const finished = boxes.every((owner) => owner !== null);
		return {
			ok: true,
			state: {
				...state,
				h,
				v,
				boxes,
				scores,
				lastEdge: { kind: move.edge, index: move.index },
				// Closing a box earns another turn.
				turn: finished ? null : claimed > 0 ? player : otherPlayer(player),
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: DotsBoxesMove[] = [];
		state.h.forEach((owner, index) => {
			if (owner === null) moves.push({ edge: "h", index });
		});
		state.v.forEach((owner, index) => {
			if (owner === null) moves.push({ edge: "v", index });
		});
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
